import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import "moment/locale/th";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import Chip from "@material-ui/core/Chip";

// Core components
import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import TSSelect2 from "components/Forms/TSSelect2";

const useStyles = makeStyles({
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none",
  },
  tableResponsive: {
    width: "100%",
    overflowX: "auto",
  },
  statusPending: {
    backgroundColor: "#ff9800",
    color: "#fff",
    fontWeight: "bold",
  },
  statusApproved: {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: "bold",
  },
  statusRejected: {
    backgroundColor: "#f44336",
    color: "#fff",
    fontWeight: "bold",
  },
  statusCancelled: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
    fontWeight: "bold",
  },
});

const MyAttendance = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const basePath = location.pathname.startsWith("/leave") ? "/leave/attendance" : "/hr/attendance";

  let currentThaiYear = parseInt(moment().format("BBBB"));
  if (isNaN(currentThaiYear)) {
    currentThaiYear = parseInt(moment().format("YYYY")) + 543;
  }
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentThaiYear);

  const yearOptions = [
    { value: currentThaiYear + 1, label: `${currentThaiYear + 1}` },
    { value: currentThaiYear, label: `${currentThaiYear}` },
    { value: currentThaiYear - 1, label: `${currentThaiYear - 1}` },
    { value: currentThaiYear - 2, label: `${currentThaiYear - 2}` },
  ];

  const fetchHistory = (year) => {
    if (!year || isNaN(year)) return;
    setLoading(true);
    axios
      .get(`/api/v1/timeattendance/myheader?Year=${year}`)
      .then((res) => {
        if (res.data?.data) {
          setList(res.data.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchHistory(selectedYear);
  }, [selectedYear]);

  const renderStatus = (item) => {
    if (item.isCancel) {
      return <Chip label="ยกเลิกแล้ว" className={classes.statusCancelled} size="small" />;
    }
    if (item.requestStatusKey === 2 || item.isApprove === true) {
      return <Chip label="อนุมัติสมบูรณ์" className={classes.statusApproved} size="small" />;
    }
    if (item.requestStatusKey === -1 || item.isApprove === false) {
      return <Chip label="ไม่อนุมัติ / ปฏิเสธ" className={classes.statusRejected} size="small" />;
    }
    return <Chip label="รอดำเนินการ" className={classes.statusPending} size="small" />;
  };

  const getTypeText = (row) => {
    if (row.category?.categoryName) {
      return (
        <div>
          <div style={{ fontWeight: "bold", color: row.category.categoryMode === 10 ? "#2e7d32" : "#e65100" }}>
            {row.category.categoryName}
          </div>
          {row.durationHours ? (
            <div style={{ fontSize: "12px", color: "#1976d2", fontWeight: "bold" }}>
              ({row.durationHours} ชม.)
            </div>
          ) : null}
        </div>
      );
    }
    if (row.attendanceType === 1) return "ลงเวลาเข้า (กจ. 1)";
    if (row.attendanceType === 2) return "ลงเวลาออก (กจ. 4)";
    if (row.attendanceType === 3) return "ทั้งเข้าและออก (กจ. 1 / กจ. 4)";
    if (row.attendanceType === 10) return "ขอตามโควต้า";
    return "-";
  };

  return (
    <div>
      <Grid>
        <Col xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>ประวัติการลงเวลาปฏิบัติราชการของฉัน</h4>
                  <p className={classes.cardCategoryWhite}>
                    รายการบันทึกขอลงเวลาสำหรับวันที่ไม่ได้สแกนลายนิ้วมือ และการขออนุญาตตามโควต้า
                  </p>
                </div>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => history.push(`${basePath}/request`)}
                >
                  <i className="fa fa-plus mr-1"></i> เขียนคำขอใหม่
                </Button>
              </div>
            </CardHeader>

            <CardBody>
              <Grid style={{ marginBottom: "15px" }}>
                <Col xs={12} sm={4} md={3}>
                  <TSSelect2
                    label="เลือกปี พ.ศ."
                    options={yearOptions}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.value)}
                  />
                </Col>
              </Grid>

              <div className={classes.tableResponsive}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold" }}>ลำดับ</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>วันที่ปฏิบัติงาน</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>ประเภท</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>เวลา</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>เหตุผล</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>พยาน / หัวหน้า 1 ระดับ</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>สถานะ</TableCell>
                      <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>จัดการ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <i className="fa fa-spinner fa-spin fa-2x"></i> กำลังโหลดข้อมูล...
                        </TableCell>
                      </TableRow>
                    ) : list.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" style={{ color: "#888" }}>
                          ไม่พบประวัติการขอลงเวลาในปีนี้
                        </TableCell>
                      </TableRow>
                    ) : (
                      list.map((row, idx) => (
                        <TableRow key={row.timeAttendanceRequestID || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>
                            {moment(row.workDate || row.checkInDT || row.checkOutDT).format("D MMMM BBBB")}
                          </TableCell>
                          <TableCell>{getTypeText(row)}</TableCell>
                          <TableCell>
                            {row.category?.categoryMode === 10 || row.attendanceType === 10 ? (
                              <span>
                                {row.checkInDT && row.checkOutDT
                                  ? `${moment(row.checkInDT).format("HH:mm")} - ${moment(row.checkOutDT).format("HH:mm")} น.`
                                  : "-"}
                              </span>
                            ) : (
                              <span>
                                {row.checkInDT && `เข้า: ${moment(row.checkInDT).format("HH:mm")} น. `}
                                {row.checkOutDT && `ออก: ${moment(row.checkOutDT).format("HH:mm")} น.`}
                              </span>
                            )}
                          </TableCell>
                          <TableCell style={{ maxWidth: "200px" }}>{row.reason}</TableCell>
                          <TableCell>
                            <small>
                              {row.witnessUserName ? (
                                <>
                                  พยาน: {row.witnessUser ? `${row.witnessUser.firstName} ${row.witnessUser.lastName}` : row.witnessUserName}
                                  <br />
                                </>
                              ) : null}
                              หัวหน้า: {row.departmentBossUser ? `${row.departmentBossUser.firstName} ${row.departmentBossUser.lastName}` :
                                (row.groupWorkBossUser ? `${row.groupWorkBossUser.firstName} ${row.groupWorkBossUser.lastName}` :
                                (row.missionBossUser ? `${row.missionBossUser.firstName} ${row.missionBossUser.lastName}` :
                                (row.bossUser ? `${row.bossUser.firstName} ${row.bossUser.lastName}` : (row.bossUserName || "-"))))}
                            </small>
                          </TableCell>
                          <TableCell>{renderStatus(row)}</TableCell>
                          <TableCell align="center">
                            <Button
                              color="info"
                              size="sm"
                              round
                              justIcon
                              title="ดูรายละเอียด / ติดตามสถานะ"
                              onClick={() =>
                                history.push(`${basePath}/request/${row.timeAttendanceRequestID}`)
                              }
                            >
                              <i className="fa fa-eye"></i>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Grid>
    </div>
  );
};

export default MyAttendance;
